/* Regios Discounts price sync for theme-rendered surfaces the app embed
   cannot cover reliably (PDP + sticky, tiles, search, cart fragments).

   Flow:
   1. Read JSON payloads from snippets/regios-dopp-sync.liquid
   2. Ask DOPP calculateDiscountedPrices
   3. Cache real discounts by productId:variantId
   4. Paint sale / compare / percent into theme hooks and force sale layout
   5. Re-assert from cache whenever the embed mutates the DOM

   Sold-out / market-OOS (DE Haydon) still gets a real discount when DOPP
   returns one — same sale stack as in-stock US. Only when there is NO real
   discount do we force a single regular row. */
(() => {
  'use strict';

  const SEL = 'script[type="application/json"][data-regios-dopp-sync]:not([data-regios-synced])';
  const PAYLOAD_SEL = 'script[type="application/json"][data-regios-dopp-sync]';
  const ON_SALE_CONTAINER = 'regios-dopp-generic-price--on-sale';
  const GENERIC_ITEM_SEL =
    '.regios-dopp-generic-price-item--sale, .regios-dopp-generic-price-item--regular';

  let api = null;
  let reassertRaf = 0;
  const discountCache = new Map(); // key -> { saleAmount, saleText, regularAmount, regularText, percentText }
  const painted = new WeakMap();

  const cacheKey = (productId, variantId) => `${productId}:${variantId}`;

  /* Currency-agnostic: armada-money.liquid renders suffix currencies as well
     ("0,00 kr", "0,00 zł", "0,00 Kč") and CHF as "CHF 0,00", none of which a
     symbol-prefix pattern matches. Compare on digits instead. */
  const isZeroMoneyText = (text) => {
    const digits = String(text ?? '').replace(/\D/g, '');
    return digits !== '' && /^0+$/.test(digits);
  };

  const discountFromResult = (res) =>
    !!(res && res.salePrice && res.regularPrice && res.salePrice.amount < res.regularPrice.amount);

  const themeMoney = (amount, fallback) => {
    const fmt = window.theme?.settings?.money_with_currency_format;
    if (typeof formatMoney === 'function' && fmt && typeof amount === 'number') {
      const tmp = document.createElement('div');
      tmp.innerHTML = formatMoney(Math.round(amount * 100), fmt);
      return tmp.textContent;
    }
    return fallback;
  };

  const formatGuard = new MutationObserver((records) => {
    for (const r of records) {
      let node = r.target.nodeType === 1 ? r.target : r.target.parentNode;
      while (node && node.nodeType === 1) {
        if (painted.has(node)) {
          const want = painted.get(node);
          if (node.textContent !== want) node.textContent = want;
          break;
        }
        node = node.parentNode;
      }
    }
  });

  const setCellText = (el, text) => {
    if (!el || text == null) return;
    painted.set(el, text);
    if (el.textContent !== text) el.textContent = text;
    formatGuard.observe(el, { childList: true, characterData: true, subtree: true });
  };

  const assertDiscountLayout = (priceRoot) => {
    if (!priceRoot) return;
    const container = priceRoot.querySelector('.regios-dopp-generic-price-container');
    priceRoot.classList.add('price--on-sale');
    container?.classList.add(ON_SALE_CONTAINER);
    const saleBlock = priceRoot.querySelector('.price__sale');
    const regularBlock = priceRoot.querySelector('.price__regular');
    saleBlock?.style.setProperty('display', 'flex', 'important');
    regularBlock?.style.setProperty('display', 'none', 'important');
  };

  const assertRegularLayout = (priceRoot) => {
    if (!priceRoot) return;
    /* A native compare_at sale is rendered by Liquid and owned by the theme, not
       by the app. "No Regios discount" says nothing about it, so tearing the sale
       stack down here would wipe a real strike-through after first paint — which
       is what happened on sold-out discounted variants (AAS-4965). */
    if (priceRoot.dataset?.nativeSale === 'true') return;
    const container = priceRoot.querySelector('.regios-dopp-generic-price-container');
    priceRoot.classList.remove('price--on-sale');
    container?.classList.remove(ON_SALE_CONTAINER, 'price--on-sale');
    const saleBlock = priceRoot.querySelector('.price__sale');
    const regularBlock = priceRoot.querySelector('.price__regular');
    saleBlock?.style.setProperty('display', 'none', 'important');
    regularBlock?.style.setProperty('display', 'block', 'important');
    const compareEl = saleBlock?.querySelector('.regios-dopp-generic-price-item--regular');
    const percentEl = saleBlock?.querySelector('.regios-dopp-generic-percent');
    if (compareEl) {
      painted.delete(compareEl);
      compareEl.textContent = '';
    }
    if (percentEl) {
      painted.delete(percentEl);
      percentEl.textContent = '';
    }
  };

  const paintDiscountOntoContainer = (container, cached) => {
    if (!container || !cached) return;
    const saleEl = container.querySelector('.regios-dopp-generic-price-item--sale');
    const regEl = container.querySelector('.regios-dopp-generic-price-item--regular');
    if (!saleEl || !regEl) return;

    setCellText(saleEl, cached.saleText);
    setCellText(regEl, cached.regularText);
    const pctEl = container.querySelector('.regios-dopp-generic-percent');
    if (pctEl && cached.percentText) setCellText(pctEl, cached.percentText);

    container.classList.add(ON_SALE_CONTAINER);

    const priceRoot = container.closest('.price');
    if (priceRoot) {
      assertDiscountLayout(priceRoot);
      return;
    }

    // Tiles: reveal app pair
    const app = container.querySelector('.product-tile__price-app');
    if (app instanceof HTMLElement) app.style.removeProperty('display');
  };

  const cacheFromResult = (productId, variantId, res) => {
    const pct =
      res.regularPrice.amount > 0
        ? Math.round((1 - res.salePrice.amount / res.regularPrice.amount) * 100)
        : 0;
    const entry = {
      saleAmount: res.salePrice.amount,
      saleText: themeMoney(res.salePrice.amount, res.salePrice.formatted),
      regularAmount: res.regularPrice.amount,
      regularText: themeMoney(res.regularPrice.amount, res.regularPrice.formatted),
      percentText: pct > 0 ? `- ${pct}%` : '',
    };
    discountCache.set(cacheKey(productId, variantId), entry);
    return entry;
  };

  const payloadVariantId = (p) => {
    if (p?.variantId != null) return p.variantId;
    return p?.variants?.[0]?.id;
  };

  const payloadIsSoldOut = (p) => {
    if (!p || !Array.isArray(p.variants) || !p.variants.length) return false;
    const id = payloadVariantId(p);
    const match = (id != null && p.variants.find((v) => v.id === id)) || p.variants[0];
    return !!(match && match.isOutOfStock);
  };

  const findCachedForContainer = (container) => {
    const host =
      container.closest('[data-regios-dopp-generic-product-id]') ||
      (container.hasAttribute('data-regios-dopp-generic-product-id') ? container : null);
    const productId = host?.getAttribute('data-regios-dopp-generic-product-id');
    if (!productId) return null;
    const sectionId =
      host?.getAttribute('data-section') || container.getAttribute('data-section');
    let variantId = null;
    if (sectionId) {
      const vs = document.getElementById(`variant-selects-${sectionId}`);
      if (vs) {
        try {
          const variants = JSON.parse(vs.querySelector('[type="application/json"]').textContent);
          const options = Array.from(vs.querySelectorAll('select, fieldset'), (element) => {
            if (element.tagName === 'SELECT') return element.value;
            if (element.tagName === 'FIELDSET') {
              return Array.from(element.querySelectorAll('input[type="radio"]')).find((radio) => radio.checked)
                ?.value;
            }
            return undefined;
          });
          const match = variants.find((variant) =>
            variant.options.every((option, index) => options[index] === option)
          );
          variantId = match?.id;
        } catch (e) {
          /* ignore */
        }
      }
    }
    if (variantId == null) {
      const tag =
        host?.querySelector(PAYLOAD_SEL) ||
        container.querySelector(PAYLOAD_SEL) ||
        container.closest('[data-regios-sync-root]')?.querySelector(PAYLOAD_SEL);
      if (tag) {
        try {
          const list = JSON.parse(tag.textContent);
          const p = Array.isArray(list) ? list[0] : null;
          variantId = payloadVariantId(p);
        } catch (e) {
          /* ignore */
        }
      }
    }
    if (variantId == null) return null;
    return discountCache.get(cacheKey(productId, variantId)) || null;
  };

  const paintCell = (cell, cached) => {
    const container =
      cell.matches?.('.regios-dopp-generic-price-container')
        ? cell
        : cell.querySelector?.('.regios-dopp-generic-price-container');
    if (!container) return;
    paintDiscountOntoContainer(container, cached);
  };

  const productIdSelector = (productId) =>
    `[data-regios-dopp-generic-product-id="${CSS.escape(String(productId))}"]`;

  const syncProduct = async (root, p) => {
    const variantId = payloadVariantId(p);
    if (p.productId == null || variantId == null) return false;

    const arg = { ...p, quantity: 1, variantId };
    if (window.RegiosDOPP?.customer) arg.customer = window.RegiosDOPP.customer;

    let res;
    try {
      res = await api.calculateDiscountedPrices(arg);
    } catch (e) {
      discountCache.delete(cacheKey(p.productId, variantId));
      if (payloadIsSoldOut(p)) {
        root.querySelectorAll?.('.price')?.forEach(assertRegularLayout);
        if (root.matches?.('.price')) assertRegularLayout(root);
      }
      return false;
    }

    if (!discountFromResult(res)) {
      discountCache.delete(cacheKey(p.productId, variantId));
      // Clear embed €0,00 compare hooks under this root
      root.querySelectorAll?.('.regios-dopp-generic-price-item--regular')?.forEach((el) => {
        if (isZeroMoneyText(el.textContent)) {
          painted.delete(el);
          el.textContent = '';
        }
      });
      if (payloadIsSoldOut(p)) {
        const sel = productIdSelector(p.productId);
        const own = root.matches?.(sel) ? [root] : [];
        const cells = own.concat(Array.from(root.querySelectorAll?.(sel) || []));
        for (const cell of cells) {
          cell.querySelectorAll?.('.price')?.forEach(assertRegularLayout);
          if (cell.matches?.('.price')) assertRegularLayout(cell);
        }
      }
      return false;
    }

    const cached = cacheFromResult(p.productId, variantId, res);
    const sel = productIdSelector(p.productId);
    const own = root.matches?.(sel) ? [root] : [];
    const cells = own.concat(Array.from(root.querySelectorAll?.(sel) || []));
    for (const cell of cells) paintCell(cell, cached);
    // Also paint plain .price trees nested under the sync root (PDP main/sticky).
    root.querySelectorAll?.('.regios-dopp-generic-price-container')?.forEach((container) => {
      const pid = container.getAttribute('data-regios-dopp-generic-product-id');
      if (pid && String(pid) !== String(p.productId)) return;
      if (!pid && !container.closest(sel)) {
        // PDP host may put product id on an ancestor
        const host = container.closest('[data-regios-dopp-generic-product-id]');
        if (host && String(host.getAttribute('data-regios-dopp-generic-product-id')) !== String(p.productId)) {
          return;
        }
      }
      paintDiscountOntoContainer(container, cached);
    });
    return true;
  };

  const syncTag = (tag) => {
    tag.setAttribute('data-regios-synced', 'true');
    let list;
    try {
      list = JSON.parse(tag.textContent);
    } catch (e) {
      return;
    }
    const root = tag.closest('[data-regios-sync-root]') || tag.parentElement;
    if (!root || !Array.isArray(list)) return;
    for (const p of list) {
      if (!p || !p.productId) continue;
      syncProduct(root, p);
    }
  };

  const syncAll = (scope) => {
    (scope || document).querySelectorAll(SEL).forEach(syncTag);
  };

  /* Re-apply cached discounts after embed mutations. This is the bulletproof
     path: once DOPP returned a discount, the embed cannot permanently wipe it. */
  const reassertFromCache = () => {
    document.querySelectorAll('.regios-dopp-generic-price-container').forEach((container) => {
      const cached = findCachedForContainer(container);
      if (cached) {
        paintDiscountOntoContainer(container, cached);
        return;
      }
      // No cached discount: clear zero compare hooks; sold-out → single row.
      const compare = container.querySelector('.regios-dopp-generic-price-item--regular');
      if (compare && isZeroMoneyText(compare.textContent)) {
        painted.delete(compare);
        compare.textContent = '';
      }
      const priceRoot = container.closest('.price');
      if (!priceRoot?.classList.contains('price--sold-out')) return;
      // Sold-out without a cached discount must not keep a fake sale stack.
      const saleEl = container.querySelector('.regios-dopp-generic-price-item--sale');
      const regEl = container.querySelector('.regios-dopp-generic-price-item--regular');
      const saleText = saleEl?.textContent?.trim();
      const regText = regEl?.textContent?.trim();
      const looksFake =
        container.classList.contains(ON_SALE_CONTAINER) &&
        (!regText || isZeroMoneyText(regText) || saleText === regText);
      if (looksFake || !container.classList.contains(ON_SALE_CONTAINER)) {
        assertRegularLayout(priceRoot);
      }
    });
  };

  /* True when a mutation record's node sits in (or brings with it) price markup.
     `deep` also scans the node's own subtree — worth it for newly added nodes,
     skipped for attribute/characterData targets, where the embed always stamps
     the price element itself or one of its children. */
  const PRICE_SCOPE_SEL = '.regios-dopp-generic-price-container, .price';

  const touchesPriceSurface = (node, deep) => {
    if (!node) return false;
    const el = node.nodeType === 1 ? node : node.parentElement;
    if (!el?.closest) return false;
    if (el.closest(PRICE_SCOPE_SEL)) return true;
    return !!(deep && el.querySelector?.(PRICE_SCOPE_SEL));
  };

  const requestReassert = () => {
    if (reassertRaf) return;
    reassertRaf = window.requestAnimationFrame(() => {
      reassertRaf = 0;
      reassertFromCache();
    });
  };

  const scheduleReassertWindow = () => {
    requestReassert();
    [50, 150, 300, 600, 1000, 2000, 3500, 5000, 8000].forEach((delay) => {
      window.setTimeout(reassertFromCache, delay);
    });
  };

  const getVariantSelectsVariant = (sectionId) => {
    const vs = document.getElementById(`variant-selects-${sectionId}`);
    if (!vs) return null;
    let variants;
    try {
      variants = JSON.parse(vs.querySelector('[type="application/json"]').textContent);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(variants) || !variants.length) return null;
    const options = Array.from(vs.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') return element.value;
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input[type="radio"]')).find((radio) => radio.checked)
          ?.value;
      }
      return undefined;
    });
    return (
      variants.find((variant) => variant.options.every((option, index) => options[index] === option)) ||
      null
    );
  };

  const clearPricePaint = (sectionId) => {
    if (sectionId == null) return;
    document
      .querySelectorAll(
        `#price-${sectionId} .regios-dopp-generic-price-item--sale, #price-${sectionId} .regios-dopp-generic-price-item--regular, #price-${sectionId} .regios-dopp-generic-percent, #price-${sectionId}-sticky .regios-dopp-generic-price-item--sale, #price-${sectionId}-sticky .regios-dopp-generic-price-item--regular, #price-${sectionId}-sticky .regios-dopp-generic-percent`
      )
      .forEach((el) => painted.delete(el));
  };

  const resyncAvailablePrice = (sectionId) => {
    const vs = document.getElementById(`variant-selects-${sectionId}`);
    if (!vs || typeof vs.syncPriceWithVariant !== 'function') return;
    vs.syncPriceWithVariant(sectionId);
    if (typeof vs.scheduleAvailablePriceSync === 'function') {
      vs.scheduleAvailablePriceSync(sectionId);
    }
  };

  const repaintVariant = async (sectionId, variant) => {
    if (!api || sectionId == null || !variant || variant.id == null) return;
    clearPricePaint(sectionId);
    if (variant.available) resyncAvailablePrice(sectionId);

    const containers = document.querySelectorAll(
      `[data-regios-dopp-generic-product-id][data-section="${CSS.escape(String(sectionId))}"]`
    );
    let paintedDiscount = false;
    for (const container of containers) {
      const tag = container.querySelector(PAYLOAD_SEL);
      if (!tag) continue;
      let list;
      try {
        list = JSON.parse(tag.textContent);
      } catch (e) {
        continue;
      }
      const p = Array.isArray(list) ? list[0] : null;
      if (!p) continue;
      // Drop stale cache for previous variant under this product
      if (p.productId != null) {
        for (const key of [...discountCache.keys()]) {
          if (key.startsWith(`${p.productId}:`) && key !== cacheKey(p.productId, variant.id)) {
            discountCache.delete(key);
          }
        }
      }
      if (await syncProduct(container, { ...p, variantId: variant.id })) {
        paintedDiscount = true;
      }
    }

    if (!paintedDiscount) {
      if (variant.available) resyncAvailablePrice(sectionId);
      else {
        document
          .querySelectorAll(`#price-${sectionId} .price, #price-${sectionId}-sticky .price`)
          .forEach(assertRegularLayout);
      }
    }
    scheduleReassertWindow();
  };

  const themedPriceText = (text) => {
    if (typeof window.armadaFormatMoney !== 'function') return text;
    const digits = text.replace(/\D/g, '');
    if (digits === '') return text;
    const cents = parseInt(digits, 10);
    return window.armadaFormatMoney(text.includes('-') ? -cents : cents);
  };

  const seedGuard = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll(GENERIC_ITEM_SEL).forEach((el) => {
      if (painted.has(el)) return;
      // Don't lock sold-out hooks until a real discount paint owns them.
      if (el.closest('.price--sold-out') && !el.closest('.price--on-sale')) return;
      setCellText(el, themedPriceText(el.textContent));
    });
  };

  const start = () => {
    api = window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0;
    if (!api) return;
    seedGuard();
    syncAll();
    scheduleReassertWindow();

    new MutationObserver((muts) => {
      let needsSync = false;
      let needsReassert = false;
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(GENERIC_ITEM_SEL)) seedGuard(n.parentElement || document);
          else if (n.querySelectorAll) seedGuard(n);
          if (n.matches && n.matches(SEL)) {
            syncTag(n);
            needsSync = true;
          } else if (n.querySelectorAll && n.querySelector(SEL)) {
            syncAll(n);
            needsSync = true;
          }
          if (!needsReassert && touchesPriceSurface(n, true)) needsReassert = true;
        }
        /* Reassert only for mutations that actually landed on price markup.
           reassertFromCache() walks every price container and re-parses variant
           JSON per container, so firing it on unrelated page churn (carousels,
           scroll-reveal classes, lazy loads) would cost a full re-scan every
           frame on a collection page. */
        if (!needsReassert && m.type !== 'childList' && touchesPriceSurface(m.target, false)) {
          needsReassert = true;
        }
      }
      if (needsSync) scheduleReassertWindow();
      else if (needsReassert) requestReassert();
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
      characterData: true,
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const { data: d } = event || {};
        if (!d?.variant) return;
        // repaintVariant() already does clearPricePaint + resyncAvailablePrice.
        repaintVariant(d.sectionId, d.variant);
      });
    }
  };

  seedGuard();

  if (window.RegiosDOPP?.api?.v0) start();
  else window.addEventListener('regios-dopp:api-initialized', start, { once: true });
})();
